import type { GpuContext } from '../context.js';
import { workgroupCount } from '../context.js';
import type { GpuImage } from '../resources.js';
import { Struct } from '../uniform-writer.js';
import { minMax } from './reduce.js';

/**
 * Rescales a raw saliency map to [0, 1] using its own extrema.
 *
 * Matches BRIA's reference post-processing for RMBG-1.4. Both the reduction and
 * the rescale stay on the GPU, so this costs two extra dispatches and no stall.
 */
export function normalizeMatte(
  ctx: GpuContext,
  src: GpuImage,
  dst: GpuImage,
  enabled = true,
): void {
  const stats = minMax(ctx, src, 0);
  ctx.dispatch(
    {
      module: 'matte_normalize.wgsl',
      bindings: [
        { kind: 'texture', format: src.format },
        { kind: 'buffer', access: 'read-only' },
        { kind: 'storageTexture', format: 'r32float' },
        { kind: 'uniform' },
      ],
    },
    [
      src,
      stats,
      dst,
      ctx.uniform(new Struct(16).i(0, src.width, src.height).u(8, enabled ? 1 : 0, 0).bytes),
    ],
    [workgroupCount(dst.width, 8), workgroupCount(dst.height, 8)],
    'matte:normalize',
  );
  ctx.releaseBuffer(stats);
}

export interface MatteFinishOptions {
  /** Alpha values below `low` go transparent, above `high` go opaque. */
  low?: number;
  high?: number;
  /** Gamma applied after the levels remap; > 1 thins the edge, < 1 thickens it. */
  gamma?: number;
  /** Overall alpha multiplier. */
  gain?: number;
  /** Manual override map (-1 erase / +1 restore / 0 untouched). */
  paint?: GpuImage | null;
  /** How strongly the override map wins over the network. */
  paintStrength?: number;
  /** Estimated background colour used for decontamination. */
  background?: readonly [number, number, number];
  /** 0 disables decontamination. */
  despill?: number;
  /** Write premultiplied colour (for display) rather than straight (for PNG). */
  premultiply?: boolean;
}

/**
 * Applies levels, manual paint overrides and colour decontamination in one pass.
 *
 * Keeping paint in its own texture rather than baked into alpha is what makes
 * the workflow non-destructive: after hand-cleaning an edge, every slider is
 * still live.
 */
export function finishMatte(
  ctx: GpuContext,
  color: GpuImage,
  alpha: GpuImage,
  dst: GpuImage,
  options: MatteFinishOptions = {},
): void {
  const paint = options.paint ?? alpha; // Bound but ignored when `usePaint` is 0.
  const bg = options.background ?? [1, 1, 1];
  const despill = options.despill ?? 0;

  const params = new Struct(64)
    .i(0, dst.width, dst.height)
    .f(8, options.low ?? 0.35, options.high ?? 0.65)
    .f(16, bg[0], bg[1], bg[2], 1)
    .f(32, options.gamma ?? 1, despill, options.gain ?? 1, options.paintStrength ?? 1)
    .u(48, options.paint ? 1 : 0, despill > 0 ? 1 : 0, options.premultiply ? 1 : 0, 0);

  ctx.dispatch(
    {
      module: 'matte_finish.wgsl',
      defines: { DST_FORMAT: dst.format },
      bindings: [
        { kind: 'texture', format: color.format },
        { kind: 'texture', format: alpha.format },
        { kind: 'texture', format: paint.format },
        { kind: 'storageTexture', format: dst.format },
        { kind: 'uniform' },
      ],
    },
    [color, alpha, paint, dst, ctx.uniform(params.bytes)],
    [workgroupCount(dst.width, 8), workgroupCount(dst.height, 8)],
    'matte:finish',
  );
}
